/**
 * The app icon designs, as geometry.
 *
 * One source for both sides: <AppIcon> in components/icons.tsx draws these as
 * SVG, and scripts/make-icons.mjs rasterises them into the PNG sets the Worker
 * serves (Node strips the types when the script imports this file, so keep it
 * to plain type annotations: no enums, no path aliases, no imports).
 *
 * Everything is in a 100×100 space on a full-bleed square of the theme colour.
 * "fg" is the white glyph; "bg" paints the theme colour back over it, for a
 * cut-out (the folder's text bars). Angles are degrees clockwise from 12
 * o'clock, and arcs and wedges run clockwise from `from` to `to`.
 */

export type Tone = "fg" | "bg";

export type Prim =
  | { k: "rect"; x: number; y: number; w: number; h: number; r: number; tone: Tone; a?: number }
  | { k: "circle"; cx: number; cy: number; r: number; tone: Tone; a?: number }
  /** A stroke with round caps; two that share an end make a round join. */
  | { k: "line"; x1: number; y1: number; x2: number; y2: number; w: number; tone: Tone; a?: number }
  | { k: "ring"; cx: number; cy: number; r: number; w: number; tone: Tone; a?: number }
  | { k: "arc"; cx: number; cy: number; r: number; w: number; from: number; to: number; tone: Tone; a?: number }
  | { k: "wedge"; cx: number; cy: number; r: number; from: number; to: number; tone: Tone; a?: number }
  /** The client folder outline, which is not one of the shapes above. */
  | { k: "folder"; tone: Tone; a?: number };

export type IconMark = "folder" | "ring" | "hub" | "board" | "slice" | "loop";

export const ICON_MARKS: { key: IconMark; label: string; prims: Prim[] }[] = [
  {
    key: "folder", label: "Client folder",
    prims: [
      { k: "folder", tone: "fg" },
      { k: "rect", x: 25.22, y: 42.09, w: 28.84, h: 7.72, r: 3.86, tone: "bg" },
      { k: "rect", x: 37, y: 55.09, w: 35.16, h: 7.72, r: 3.86, tone: "bg" },
    ],
  },
  {
    key: "ring", label: "Progress",
    prims: [
      { k: "arc", cx: 50, cy: 50, r: 30, w: 8, from: 10, to: 300, tone: "fg" },
      { k: "rect", x: 36, y: 36, w: 28, h: 7.5, r: 3.75, tone: "fg" },
      { k: "rect", x: 42, y: 47, w: 22, h: 7.5, r: 3.75, tone: "fg", a: 0.6 },
      { k: "rect", x: 36, y: 58, w: 16, h: 7.5, r: 3.75, tone: "fg", a: 0.32 },
    ],
  },
  {
    key: "hub", label: "Hub",
    prims: [
      { k: "line", x1: 50, y1: 30, x2: 50, y2: 56, w: 6, tone: "fg" },
      { k: "line", x1: 50, y1: 56, x2: 28, y2: 68.5, w: 6, tone: "fg" },
      { k: "line", x1: 50, y1: 56, x2: 72, y2: 68.5, w: 6, tone: "fg" },
      { k: "circle", cx: 50, cy: 26, r: 8, tone: "fg" },
      { k: "circle", cx: 50, cy: 56, r: 11, tone: "fg" },
      { k: "circle", cx: 27, cy: 69, r: 8, tone: "fg" },
      { k: "circle", cx: 73, cy: 69, r: 8, tone: "fg" },
    ],
  },
  {
    key: "board", label: "Board",
    prims: [
      { k: "rect", x: 23, y: 21, w: 13, h: 54, r: 6.5, tone: "fg" },
      { k: "rect", x: 43.5, y: 21, w: 13, h: 30, r: 6.5, tone: "fg" },
      { k: "rect", x: 64, y: 21, w: 13, h: 58, r: 6.5, tone: "fg" },
    ],
  },
  {
    key: "slice", label: "Time slice",
    prims: [
      { k: "wedge", cx: 50, cy: 50, r: 32, from: 0, to: 60, tone: "fg", a: 0.5 },
      { k: "ring", cx: 50, cy: 50, r: 28, w: 8, tone: "fg" },
    ],
  },
  {
    key: "loop", label: "Closed loop",
    prims: [
      { k: "arc", cx: 50, cy: 50, r: 28, w: 8, from: 65, to: 10, tone: "fg" },
      { k: "line", x1: 35, y1: 49, x2: 46, y2: 60, w: 8.5, tone: "fg" },
      { k: "line", x1: 46, y1: 60, x2: 72, y2: 31, w: 8.5, tone: "fg" },
    ],
  },
];

/** The folder is the standard icon: what a browser with no choice stored gets. */
export const DEFAULT_ICON_MARK: IconMark = "folder";

export function isIconMark(v: string | null | undefined): v is IconMark {
  return ICON_MARKS.some((m) => m.key === v);
}

/** The folder outline, in its original 32×32 space (scaled ×3.125 to fit). */
export const FOLDER_PATH =
  "M4.3 8.92V23.09a2.47 2.47 0 0 0 2.47 2.47H25.23a2.47 2.47 0 0 0 2.47-2.47V11.39a2.47 2.47 0 0 0-2.47-2.47H15.09L12.62 6.45H6.77a2.47 2.47 0 0 0-2.47 2.47Z";

/** A point on a circle, at an angle clockwise from 12 o'clock. */
function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const t = (deg * Math.PI) / 180;
  return [+(cx + r * Math.sin(t)).toFixed(2), +(cy - r * Math.cos(t)).toFixed(2)];
}

function sweep(from: number, to: number): number {
  return (((to - from) % 360) + 360) % 360;
}

/**
 * SVG elements for a design's glyph (no background), in the 100×100 space.
 * `fg` and `bg` are any CSS colour, including var(--…).
 */
export function markSvg(mark: IconMark, fg: string, bg: string): string {
  const m = ICON_MARKS.find((x) => x.key === mark) ?? ICON_MARKS[0];
  return m.prims.map((p) => {
    const col = p.tone === "fg" ? fg : bg;
    const op = p.a != null ? ` opacity="${p.a}"` : "";
    const fill = `fill="${col}"${op}`;
    const stroke = (w: number) => `fill="none" stroke="${col}" stroke-width="${w}" stroke-linecap="round"${op}`;
    switch (p.k) {
      case "rect": return `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="${p.r}" ${fill}/>`;
      case "circle": return `<circle cx="${p.cx}" cy="${p.cy}" r="${p.r}" ${fill}/>`;
      case "line": return `<line x1="${p.x1}" y1="${p.y1}" x2="${p.x2}" y2="${p.y2}" ${stroke(p.w)}/>`;
      case "ring": return `<circle cx="${p.cx}" cy="${p.cy}" r="${p.r}" ${stroke(p.w)}/>`;
      case "arc": {
        const [x1, y1] = polar(p.cx, p.cy, p.r, p.from);
        const [x2, y2] = polar(p.cx, p.cy, p.r, p.to);
        return `<path d="M${x1} ${y1}A${p.r} ${p.r} 0 ${sweep(p.from, p.to) > 180 ? 1 : 0} 1 ${x2} ${y2}" ${stroke(p.w)}/>`;
      }
      case "wedge": {
        const [x1, y1] = polar(p.cx, p.cy, p.r, p.from);
        const [x2, y2] = polar(p.cx, p.cy, p.r, p.to);
        return `<path d="M${p.cx} ${p.cy}L${x1} ${y1}A${p.r} ${p.r} 0 ${sweep(p.from, p.to) > 180 ? 1 : 0} 1 ${x2} ${y2}Z" ${fill}/>`;
      }
      case "folder": return `<path transform="scale(3.125)" d="${FOLDER_PATH}" ${fill}/>`;
    }
  }).join("");
}

// ---- Coverage, for the rasteriser ------------------------------------------

function inRoundedRect(px: number, py: number, x: number, y: number, w: number, h: number, r: number): boolean {
  if (px < x || py < y || px > x + w || py > y + h) return false;
  const cx = Math.min(Math.max(px, x + r), x + w - r);
  const cy = Math.min(Math.max(py, y + r), y + h - r);
  return (px - cx) ** 2 + (py - cy) ** 2 <= r * r;
}

function nearSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number, rad: number): boolean {
  const dx = x2 - x1, dy = y2 - y1;
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy || 1)));
  return (px - x1 - t * dx) ** 2 + (py - y1 - t * dy) ** 2 <= rad * rad;
}

function inAngle(px: number, py: number, cx: number, cy: number, from: number, to: number): boolean {
  const deg = ((Math.atan2(px - cx, cy - py) * 180) / Math.PI + 360) % 360;
  return sweep(from, deg) <= sweep(from, to);
}

/** The folder, from the original rasteriser: a rounded body plus a 45° tab. */
function inFolder(px: number, py: number): boolean {
  const x = px / 3.125, y = py / 3.125, R = 2.47;
  if (inRoundedRect(x, y, 4.3, 8.92, 23.4, 16.64, R)) return true;
  if (y < 6.45 || y > 8.92 + R || x < 4.3 || x > 12.62 + (y - 6.45)) return false;
  const cx = 4.3 + R, cy = 6.45 + R;
  return x >= cx || y >= cy || (x - cx) ** 2 + (y - cy) ** 2 <= R * R;
}

/** True where a shape covers the point (in the 100×100 space). */
export function covers(p: Prim, px: number, py: number): boolean {
  switch (p.k) {
    case "rect": return inRoundedRect(px, py, p.x, p.y, p.w, p.h, p.r);
    case "circle": return (px - p.cx) ** 2 + (py - p.cy) ** 2 <= p.r * p.r;
    case "line": return nearSegment(px, py, p.x1, p.y1, p.x2, p.y2, p.w / 2);
    case "ring": {
      const d = Math.hypot(px - p.cx, py - p.cy);
      return Math.abs(d - p.r) <= p.w / 2;
    }
    case "arc": {
      const d = Math.hypot(px - p.cx, py - p.cy);
      if (Math.abs(d - p.r) <= p.w / 2 && inAngle(px, py, p.cx, p.cy, p.from, p.to)) return true;
      const [x1, y1] = polar(p.cx, p.cy, p.r, p.from);
      const [x2, y2] = polar(p.cx, p.cy, p.r, p.to);
      const rr = (p.w / 2) ** 2;
      return (px - x1) ** 2 + (py - y1) ** 2 <= rr || (px - x2) ** 2 + (py - y2) ** 2 <= rr;
    }
    case "wedge":
      return (px - p.cx) ** 2 + (py - p.cy) ** 2 <= p.r * p.r && inAngle(px, py, p.cx, p.cy, p.from, p.to);
    case "folder": return inFolder(px, py);
  }
}
